<template>
    <div class="forum-home panel">
        <div class="panel-body">
            <header>
                <div class="flex">
                    <div class="flex-full">
                        <h1>Forum Home</h1>
                    </div>
                    <div class="action-bar flex-none">
                        <router-link :to="searchUrl">
                            <i class="fa fa-search"></i>
                        </router-link>
                    </div>
                </div>
            </header>

            <div class="thread-controls flex">
                <div class="flex flex-full">
                    <TabSelect
                        :options="threadSubsetOptions"
                        :value="threadSubset"
                        @input="setThreadSubset($event)"
                    />
                    <DropdownSelect
                        :options="threadSubsetPeriodOptions"
                        :value="threadSubsetPeriod"
                        @input="setThreadSubsetPeriod($event)"
                        v-if="threadSubset === 'top'"
                    />
                </div>

                <div class="flex-none">
                    <router-link :to="newThreadUrl">
                        <i class="fa fa-plus"></i>
                        Create Thread
                    </router-link>
                </div>
            </div>

            <ForumHomeCategories v-if="threadSubset === 'categories'" />

            <ForumHomeLatest
                v-if="threadSubset === 'latest'"
                :sort-order="threadSortOrder"
                @update:sort-order="threadSortOrder = $event"
            />

            <ForumHomeTop
                v-if="threadSubset === 'top'"
                :period="threadSubsetPeriod"
                :sort-order="threadSortOrder"
                @update:sort-order="threadSortOrder = $event"
            />
        </div>
    </div>
</template>

<script>
    // ForumHome: the home page for the forum. Allows a user to browse the forum
    // by latest threads or by category.

    import ForumUtils          from '../utils/forum';
    import DropdownSelect      from './reusable/forum/dropdown-select';
    import Loader              from './reusable/forum/loader';
    import TabSelect           from './reusable/forum/tab-select';
    import ForumHomeCategories from './widgets/ForumHomeCategories';
    import ForumHomeLatest     from './widgets/ForumHomeLatest';
    import ForumHomeTop        from './widgets/ForumHomeTop';
    import ForumCategoryPreview from './reusable/forum/category-preview';
    import ForumThreadList from './reusable/forum/thread-list';


    function getViewportBottom() {
        const $window = $(window);
        return $window.scrollTop() + $window.height();
    }

    function getBottomPos(elem) {
        const $elem = $(elem);
        return $elem.offset().top + $elem.outerHeight();
    }


    export default {
        props: {
            threadSubset: {
                type: String,
                default: 'latest',
                validator(value) {
                    return [
                        'categories',
                        'latest',
                        'top',
                    ].includes(value);
                },
            },

            threadSubsetPeriod: {
                type: String,
                default: 'week',
                validator(value) {
                    return [
                        'week',
                        'month',
                        'semester',
                    ].includes(value);
                },
            },
        },

        components: {
            DropdownSelect,
            Loader,
            TabSelect,
            ForumHomeCategories,
            ForumHomeLatest,
            ForumHomeTop,
        },

        data() {
            return {
                threadSortOrder: 'default',
                threadSubsetOptions: [
                    { value: 'categories', label: 'Categories' },
                    { value: 'latest', label: 'Latest' },
                    { value: 'top', label: 'Top' },
                ],
                threadSubsetPeriodOptions: [
                    { value: 'week', label: 'This week' },
                    { value: 'month', label: 'This month' },
                    { value: 'semester', label: 'This semester' },
                ],
            };
        },

        mounted() {
            this.loadFiltersFromUrl();
            this.syncFiltersToUrl();
        },

        methods: {
            loadFiltersFromUrl() {
                const query = this.$route.query;

                if (query['sort']) {
                    const value = query['sort'].toString().trim();
                    this.threadSortOrder = ForumUtils.sortOrderFromUrl(value);
                }
            },

            syncFiltersToUrl() {
                const setQueryParam = (name, value) => {
                    // Need to clone the current query in order for router to
                    // detect change
                    const query = Object.assign({}, this.$route.query);
                    if (value === null) {
                        delete query[name];
                    } else {
                        query[name] = value;
                    }
                    this.$router.replace({ query });
                }

                this.$watch('threadSortOrder', function (value) {
                    if (value !== 'default') {
                        setQueryParam('sort', ForumUtils.sortOrderToUrl(value));
                    } else {
                        setQueryParam('sort', null);
                    }
                });
            },

            /**
             * Copies $route object, changes the name, and pushes to $router.
             * Used by setThreadSubsetPeriod() to copy query parameters, etc.
             */
            changeRouteName(name) {
                const newRoute = Object.assign({}, this.$route);
                newRoute.path = null;
                newRoute.name = name;
                this.$router.push(newRoute);
            },

            setThreadSubset(subset) {
                // Do not use changeRouteName(), as we want to reset query
                // parameters between subsets.
                switch (subset) {
                case 'categories':
                    this.$router.push({ name: 'forum-categories' });
                    break;
                case 'latest':
                    this.$router.push({ name: 'forum-latest' });
                    break;
                case 'top':
                    this.$router.push({ name: 'forum-top' });
                    break;
                default:
                    throw new Error('unknown thread subset: ' + subset);
                }
            },

            setThreadSubsetPeriod(period) {
                switch (period) {
                case 'week':
                    this.changeRouteName('forum-top-week');
                    break;
                case 'month':
                    this.changeRouteName('forum-top-month');
                    break;
                case 'semester':
                    this.changeRouteName('forum-top-semester');
                    break;
                default:
                    throw new Error('unknown thread subset period: ' + period);
                }
            },
        },

        computed: {
            searchUrl() {
                return {
                    name: 'forum-search',
                };
            },

            newThreadUrl() {
                return {
                    name: 'new-forum-thread',
                };
            },
        },

        watch: {
            '$route.path': function () {
                if (this.threadSortOrder !== 'default') {
                    this.threadSortOrder = 'default';
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    .flex {
        display: flex;
    }
    .flex-full {
        flex: 1;
    }
    .flex-none {
        flex: 0;
    }

    .forum-home .panel-body {
        position: relative;
        margin: 20px 0;
        padding: 40px;
    }

    header {
        h1 {
            margin: 0;
            font-size: 38px;
        }

        .action-bar {
            line-height: 40px;

            a {
                display: block;
                font-size: 24px;
                color: #b0b0b0;
                width: 40px;
                text-align: center;

                &:hover {
                    background: #363636;
                }

                i {
                    position: relative;
                    top: -2px;
                }
            }
        }
    }

    .thread-controls {
        margin-top: 30px;

        a {
            background: #363636;
            border: none;
            color: #b0b0b0;
            display: inline-block;
            white-space: nowrap;
            height: 40px;
            line-height: 40px;
            padding: 0 12px;
            text-decoration: none;

            &:hover {
                background: #b0b0b0;
                color: #363636;
            }

            i {
                margin-right: 3px;
            }
        }

        .dropdown-select {
            //margin-top: 20px;
        }
    }
</style>

<style lang="scss">
    .forum-home {
        .forum-dropdown-select.dropdown-select {
            margin-left: 20px;
        }
    }
</style>



// WEBPACK FOOTER //
// ForumHome.vue?f1164914